import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Lock, Mail, Eye, EyeOff } from 'lucide-react';

const setPasswordSchema = z
  .object({
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
      .regex(/[0-9]/, 'Password must contain at least one number')
      .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character'),
    confirm_password: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirm_password, {
    message: 'Passwords do not match',
    path: ['confirm_password'],
  });

export type SetPasswordFormData = z.infer<typeof setPasswordSchema>;

export interface SetPasswordStepHandle {
  triggerValidation: () => Promise<boolean>;
}

interface SetPasswordStepProps {
  ownerEmail: string;
  password: string;
  confirmPassword: string;
  onUpdate: (data: { password: string; confirm_password: string }) => void;
  onValidationChange?: (isValid: boolean) => void;
}

export const SetPasswordStep = forwardRef<SetPasswordStepHandle, SetPasswordStepProps>(
  ({ ownerEmail, password, confirmPassword, onUpdate, onValidationChange }, ref) => {
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const form = useForm<SetPasswordFormData>({
      resolver: zodResolver(setPasswordSchema),
      defaultValues: {
        password: password || '',
        confirm_password: confirmPassword || '',
      },
      mode: 'onChange',
    });

    React.useEffect(() => {
      const subscription = form.watch((values) => {
        onUpdate({
          password: values.password || '',
          confirm_password: values.confirm_password || '',
        });
      });
      return () => subscription.unsubscribe();
    }, [form, onUpdate]);

    React.useEffect(() => {
      onValidationChange?.(form.formState.isValid);
    }, [form.formState.isValid, onValidationChange]);

    useImperativeHandle(ref, () => ({
      triggerValidation: () => form.trigger().then((ok) => ok),
    }));

    return (
      <Form {...form}>
        <form className="space-y-5">
          {/* Account Section */}
          <div className="rounded-xl border border-gray-100 bg-slate-50 p-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase text-brand-teal tracking-wider mb-3">
              <Mail className="h-4 w-4" />
              Account
            </div>
            <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
              <p className="text-xs text-gray-500">Email (from invitation)</p>
              <p className="font-medium text-gray-900">{ownerEmail}</p>
            </div>
          </div>

          {/* Password Section */}
          <div className="rounded-xl border border-gray-100 bg-slate-50 p-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase text-brand-teal tracking-wider mb-3">
              <Lock className="h-4 w-4" />
              Set Password
            </div>
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-700 font-medium text-sm">
                      Password <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Create a password"
                          {...field}
                          className="pr-10 h-11 rounded-xl border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:border-brand-teal focus:ring-brand-teal/20"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors rounded p-1"
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="confirm_password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-700 font-medium text-sm">
                      Confirm Password <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showConfirmPassword ? 'text' : 'password'}
                          placeholder="Confirm your password"
                          {...field}
                          className="pr-10 h-11 rounded-xl border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:border-brand-teal focus:ring-brand-teal/20"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors rounded p-1"
                          aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <p className="text-xs text-gray-500">
                Password must be at least 8 characters and include uppercase, lowercase, number, and
                special character.
              </p>
            </div>
          </div>
        </form>
      </Form>
    );
  }
);

SetPasswordStep.displayName = 'SetPasswordStep';
export default SetPasswordStep;
